import { useAppSelector } from "@/store/hooks";
import type {
  CompositionElement,
  DragMatchingImageElement,
  DragMatchingTextElement,
  FillBlanksElement,
  ImageChoiceElement,
  OddImageOutElement,
  OddTextOutElement,
  OrderImageElement,
  OrderTextElement,
  SlideElement,
  SpeakingElement,
  SpellingElement,
} from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { AudioElementToolbar } from "./AudioElementToolbar";
import { BorderToolbar } from "./BorderToolbar";
import { CompositionElementToolbar } from "./CompositionElementToolbar";
import { LineElementToolbar } from "./LineElementToolbar";
import { OrderImageElementToolbar } from "./OrderImageElementToolbar";
import { OrderTextElementToolbar } from "./OrderTextElementToolbar";
import { ShapeElementToolbar } from "./ShapeElementToolbar";
import { SpellingElementToolbar } from "./SpellingElementToolbar";
import { TextElementToolbar } from "./TextElementToolbar";

type QuestionElement =
  | ImageChoiceElement
  | DragMatchingTextElement
  | DragMatchingImageElement
  | OddTextOutElement
  | OddImageOutElement
  | FillBlanksElement
  | OrderImageElement
  | OrderTextElement
  | SpellingElement
  | SpeakingElement
  | CompositionElement;

// UI-only grouping for this toolbar (border controls + display gate). Note
// `composition` belongs here even though it is NOT in questionElementSchemas —
// that enum drives scoring/completion, this one drives which toolbar shows.
function isQuestionElement(element: SlideElement): element is QuestionElement {
  return (
    element.type === "imageChoice" ||
    element.type === "dragMatchingText" ||
    element.type === "dragMatchingImage" ||
    element.type === "oddTextOut" ||
    element.type === "oddImageOut" ||
    element.type === "fillBlanks" ||
    element.type === "orderImage" ||
    element.type === "orderText" ||
    element.type === "spelling" ||
    element.type === "speaking" ||
    element.type === "composition"
  );
}

export function SelectedElementToolbar() {
  const { slide, selectedId } = useAppSelector((state) => state.slideEditor.present);
  const [isExpanded, setIsExpanded] = useState(true);

  if (!slide) return null;

  const selectedElement = slide.elements.find((el) => el.id === selectedId);

  if (!selectedElement) {
    return null;
  }

  const isTextElement = selectedElement.type === "text";
  const isShapeElement = selectedElement.type === "shape";
  const isLineElement = selectedElement.type === "line";
  const isTextChoiceElement = selectedElement.type === "textChoice";
  const isImageElement = selectedElement.type === "image";
  const isSpellingElement = selectedElement.type === "spelling";
  const isOrderImageElement = selectedElement.type === "orderImage";
  const isOrderTextElement = selectedElement.type === "orderText";
  const isCompositionElement = selectedElement.type === "composition";
  const isAudioElement = selectedElement.type === "audioPlayer";
  const isQuestion = isQuestionElement(selectedElement);

  // Drop the top-pinned bar to the bottom when the element sits in the top strip (else it covers it).
  const nearTop = ("position" in selectedElement ? (selectedElement.position?.y ?? 50) : 50) < 16;

  if (
    !(
      isImageElement ||
      isTextElement ||
      isShapeElement ||
      isLineElement ||
      isQuestion ||
      isTextChoiceElement ||
      isAudioElement
    )
  )
    return null;

  return (
    <div
      className={`fixed left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-200/80 flex items-center gap-2 px-3 py-2 z-[99] ${
        nearTop ? "bottom-5" : "top-5"
      }`}
    >
      {/* Toggle Button */}
      <IconButton
        onClick={() => setIsExpanded(!isExpanded)}
        variant="ghost"
        size="sm"
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-all duration-200"
        aria-label={isExpanded ? "Collapse toolbar" : "Expand toolbar"}
      >
        {isExpanded ? (
          <FaChevronUp className="w-3.5 h-3.5 text-gray-500" />
        ) : (
          <FaChevronDown className="w-3.5 h-3.5 text-gray-500" />
        )}
      </IconButton>

      {/* Toolbar Content - Only shown when expanded */}
      {isExpanded && (
        <>
          {/* Divider */}
          <div className="w-px h-6 bg-gray-300" />

          {/* Fill Color Picker - For shapes */}
          {isShapeElement && <ShapeElementToolbar shapeElement={selectedElement} />}

          {/* Text Controls - For text elements and text choice questions */}
          {(isTextElement || isTextChoiceElement) && (
            <TextElementToolbar textElement={selectedElement} />
          )}

          {isLineElement && <LineElementToolbar lineElement={selectedElement} />}

          {/* Word + reveal-first-letter controls - For spelling questions */}
          {isSpellingElement && <SpellingElementToolbar spellingElement={selectedElement} />}

          {/* Prompt media + grid layout - For image ordering questions */}
          {isOrderImageElement && <OrderImageElementToolbar orderImageElement={selectedElement} />}

          {/* Item text size - For text ordering questions */}
          {isOrderTextElement && <OrderTextElementToolbar orderTextElement={selectedElement} />}

          {/* Writing prompt input - For composition elements */}
          {isCompositionElement && (
            <CompositionElementToolbar compositionElement={selectedElement} />
          )}

          {/* Narration script + its TTS - For audio elements */}
          {isAudioElement && <AudioElementToolbar audioElement={selectedElement} />}

          {(isQuestion || isImageElement) && <BorderToolbar element={selectedElement} />}
        </>
      )}
    </div>
  );
}
